/**
 * Pengaturan konteks dinas untuk Super Admin / UID khusus.
 * Field pada objek user di localStorage yang diubah: `dinas_id`, `nama_dinas` (sesuai pilihan dinas),
 * serta `uid`, `dinas_id`, dan `nama_dinas` (sesuai pilihan pegawai). Field lain tidak disentuh.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import { API_ESA_OFFICE, userCanAccessPengaturanKonteksDinas } from "utils";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { logoutUser } from "actions/AuthAction";

const STORAGE_PROFIL_ASLI = "super_admin_profil_dinas_asli";

/** Salin objek user lalu set `dinas_id`, `nama_dinas`, dan/atau `uid` saja (accessToken dan field lain tidak diubah). */
function patchLocalUserDinasAndUid(existingRaw, { dinas_id, nama_dinas, uid }) {
  let base = {};
  try {
    base = existingRaw ? JSON.parse(existingRaw) : {};
  } catch {
    base = {};
  }
  const next = { ...base };
  if (dinas_id != null && dinas_id !== "") next.dinas_id = dinas_id;
  if (nama_dinas !== undefined) next.nama_dinas = nama_dinas;
  if (uid != null && uid !== "") next.uid = uid;
  return next;
}

function readProfilAsliFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_PROFIL_ASLI);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p === "object" && p.user && typeof p.user === "object") {
      return { kind: "full", user: p.user };
    }
    if (p && p.dinas_id != null) {
      return { kind: "legacy", dinas_id: p.dinas_id, nama_dinas: p.nama_dinas };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function KonteksDinasSuperAdmin() {
  const navigate = useNavigate();
  const [loadingDinas, setLoadingDinas] = useState(true);
  const [dinasList, setDinasList] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedDinas, setSelectedDinas] = useState(null);

  const [usersInDinas, setUsersInDinas] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedTargetUser, setSelectedTargetUser] = useState(null);

  const fetchDinas = async () => {
    setLoadingDinas(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}dinas`, {
        withCredentials: true,
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      const sorted = [...rows].sort((a, b) =>
        String(a.nama_dinas || "").localeCompare(String(b.nama_dinas || ""), "id")
      );
      setDinasList(sorted);
    } catch (err) {
      console.error(err);
      setDinasList([]);
      swal({
        title: "Gagal memuat dinas",
        text: "Tidak dapat mengambil daftar dinas. Coba lagi nanti.",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoadingDinas(false);
    }
  };

  const fetchUsersForDinas = useCallback(async (dinasId) => {
    if (dinasId == null || dinasId === "") {
      setUsersInDinas([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}users/dinas/${dinasId}`, {
        withCredentials: true,
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setUsersInDinas(rows);
    } catch (err) {
      console.error(err);
      setUsersInDinas([]);
      swal({
        title: "Gagal memuat pegawai",
        text: "Tidak dapat mengambil daftar user untuk dinas ini.",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (!raw) {
      logoutUser(navigate);
      return;
    }
    let u;
    try {
      u = JSON.parse(raw);
    } catch {
      logoutUser(navigate);
      return;
    }
    if (!userCanAccessPengaturanKonteksDinas(u)) {
      swal({
        title: "Akses ditolak",
        text: "Hanya Super Admin atau akun terdaftar khusus yang dapat membuka halaman ini.",
        icon: "error",
        button: "OK",
      }).then(() => navigate("/dashboards/analytics"));
      return;
    }
    setUserInfo(u);
    fetchDinas();
  }, [navigate]);

  useEffect(() => {
    if (!userInfo || !dinasList.length) return;
    const match = dinasList.find(
      (d) => String(d.dinas_id) === String(userInfo.dinas_id)
    );
    setSelectedDinas(match || null);
  }, [userInfo, dinasList]);

  useEffect(() => {
    const id = selectedDinas?.dinas_id;
    if (id == null) {
      setUsersInDinas([]);
      setSelectedTargetUser(null);
      return;
    }
    fetchUsersForDinas(id);
  }, [selectedDinas?.dinas_id, fetchUsersForDinas]);

  useEffect(() => {
    if (!userInfo || !usersInDinas.length) {
      setSelectedTargetUser(null);
      return;
    }
    const match = usersInDinas.find(
      (row) => String(row.user_id) === String(userInfo.uid)
    );
    setSelectedTargetUser(match || null);
  }, [userInfo, usersInDinas]);

  const persistUser = (nextUser) => {
    localStorage.setItem("user", JSON.stringify(nextUser));
    setUserInfo(nextUser);
    window.dispatchEvent(new Event("storage"));
  };

  const applyDinasOnlyToLocalStorage = (dinas) => {
    const raw = localStorage.getItem("user");
    if (!raw) return;
    try {
      persistUser(
        patchLocalUserDinasAndUid(raw, {
          dinas_id: dinas.dinas_id,
          nama_dinas: dinas.nama_dinas,
        })
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handlePilihDinas = (_, dinas) => {
    if (!dinas || dinas.dinas_id == null) {
      setSelectedDinas(null);
      setUsersInDinas([]);
      setSelectedTargetUser(null);
      return;
    }
    setSelectedDinas(dinas);
    applyDinasOnlyToLocalStorage(dinas);
    setSelectedTargetUser(null);
  };

  const handlePilihUser = (_, row) => {
    if (!row || row.user_id == null) {
      setSelectedTargetUser(null);
      return;
    }
    const raw = localStorage.getItem("user");
    const next = patchLocalUserDinasAndUid(raw, {
      uid: row.user_id,
      dinas_id: row.dinas_id,
      nama_dinas: row.nama_dinas,
    });
    persistUser(next);
    setSelectedTargetUser(row);
    swal({
      title: "Konteks diperbarui",
      text: `Di localStorage diubah: uid=${row.user_id}, dinas_id=${row.dinas_id}, nama_dinas sesuai pilihan. Field lain tidak berubah.`,
      icon: "success",
      timer: 2800,
      button: "OK",
    });
  };

  const handleKembaliProfilAsli = () => {
    const snap = readProfilAsliFromStorage();
    if (!snap) {
      swal({
        title: "Data profil asli tidak tersedia",
        text: "Silakan logout dan login kembali.",
        icon: "info",
        button: "OK",
      });
      return;
    }
    if (snap.kind === "full") {
      const u = { ...snap.user };
      persistUser(u);
      const dMatch = dinasList.find(
        (d) => String(d.dinas_id) === String(u.dinas_id)
      );
      setSelectedDinas(
        dMatch || {
          dinas_id: u.dinas_id,
          nama_dinas: u.nama_dinas,
        }
      );
      swal({
        title: "Dikembalikan",
        text: "Profil localStorage diset sesuai snapshot saat login.",
        icon: "success",
        timer: 2000,
        button: "OK",
      });
      return;
    }
    const match =
      dinasList.find((d) => String(d.dinas_id) === String(snap.dinas_id)) ||
      {
        dinas_id: snap.dinas_id,
        nama_dinas: snap.nama_dinas,
      };
    applyDinasOnlyToLocalStorage(match);
    setSelectedDinas(match);
    swal({
      title: "Dinas dikembalikan",
      text: "Hanya dinas dari snapshot lama (tanpa uid). Login ulang disarankan untuk snapshot lengkap.",
      icon: "success",
      timer: 2200,
      button: "OK",
    });
  };

  if (!userInfo) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={6} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const snapLabel = (() => {
    const s = readProfilAsliFromStorage();
    if (!s) return null;
    if (s.kind === "full") {
      return `${s.user?.nama || "—"} · uid ${s.user?.uid ?? "—"} · ${s.user?.nama_dinas || "—"}`;
    }
    return `${s.nama_dinas || "—"} (id: ${s.dinas_id})`;
  })();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDTypography variant="h4" fontWeight="medium" color="text">
              Pengaturan — Konteks Dinas dan User
            </MDTypography>
            <MDTypography variant="body2" color="text" opacity={0.75}>
              Pilih dinas mengubah <code>dinas_id</code> dan{" "}
              <code>nama_dinas</code>. Pilih pegawai juga mengubah <code>uid</code>{" "}
              serta menyelaraskan <code>dinas_id</code> dan <code>nama_dinas</code>{" "}
              dari data pegawai. Nama pengguna, uid_RSS, dan field lain seperti sesi
              login. Data server tidak diubah.
            </MDTypography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: "100%" }}>
              <MDTypography variant="h6" mb={2} color="text">
                Informasi akun (local storage)
              </MDTypography>
              <Divider sx={{ mb: 2 }} />
              <MDBox display="flex" flexDirection="column" gap={1}>
                <MDTypography variant="button" color="text" fontWeight="bold">
                  {userInfo.nama}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Email: {userInfo.email || "—"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  uid (user_id): {userInfo.uid ?? "—"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  uid_RSS: {userInfo.uid_RSS ?? "—"}
                </MDTypography>
                <MDBox mt={1}>
                  <Chip label={userInfo.status} color="secondary" size="small" />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: "100%" }}>
              <MDTypography variant="h6" mb={1} color="text">
                Konteks aktif
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                Nilai yang dipakai modul lain dari <code>user</code> di
                localStorage.
              </MDTypography>
              <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <Chip
                  label={`dinas_id: ${userInfo.dinas_id ?? "—"}`}
                  color="info"
                  size="medium"
                />
                <Chip
                  label={`uid: ${userInfo.uid ?? "—"}`}
                  color="info"
                  size="medium"
                  variant="outlined"
                />
                <Chip
                  label={userInfo.nama_dinas || "Nama dinas"}
                  variant="outlined"
                  size="medium"
                />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h6" mb={2} color="text">
                1. Pilih dinas
              </MDTypography>
              {loadingDinas ? (
                <MDBox display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </MDBox>
              ) : (
                <MDBox maxWidth={560}>
                  <Autocomplete
                    options={dinasList}
                    getOptionLabel={(o) =>
                      o?.nama_dinas
                        ? `${o.nama_dinas} (id: ${o.dinas_id})`
                        : String(o?.dinas_id ?? "")
                    }
                    value={selectedDinas}
                    onChange={handlePilihDinas}
                    isOptionEqualToValue={(a, b) =>
                      String(a?.dinas_id) === String(b?.dinas_id)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cari / pilih dinas"
                        variant="standard"
                      />
                    )}
                  />
                </MDBox>
              )}

              <MDBox mt={4}>
                <MDTypography variant="h6" mb={1} color="text">
                  2. Pilih pegawai (user pada dinas)
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  Hanya pegawai berstatus Aktif. Mengubah{" "}
                  <code>uid</code>, <code>dinas_id</code>, dan{" "}
                  <code>nama_dinas</code> di localStorage.
                </MDTypography>
                {!selectedDinas ? (
                  <MDTypography variant="body2" color="text" fontStyle="italic">
                    Pilih dinas terlebih dahulu.
                  </MDTypography>
                ) : loadingUsers ? (
                  <MDBox display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </MDBox>
                ) : (
                  <MDBox maxWidth={560}>
                    <Autocomplete
                      options={usersInDinas}
                      getOptionLabel={(o) =>
                        o?.nama
                          ? `${o.nama} (id: ${o.user_id})`
                          : String(o?.user_id ?? "")
                      }
                      value={selectedTargetUser}
                      onChange={handlePilihUser}
                      isOptionEqualToValue={(a, b) =>
                        String(a?.user_id) === String(b?.user_id)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cari / pilih pegawai"
                          variant="standard"
                        />
                      )}
                    />
                  </MDBox>
                )}
              </MDBox>

              {readProfilAsliFromStorage() && (
                <MDBox mt={3}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={handleKembaliProfilAsli}
                  >
                    Kembalikan ke profil saat login
                  </MDButton>
                  {snapLabel && (
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      mt={1}
                    >
                      Snapshot: {snapLabel}
                    </MDTypography>
                  )}
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default KonteksDinasSuperAdmin;
